import {calcUserScore, filterObjectByParams} from 'helpers/common-helper';
import {getDefaultExposure} from 'helpers/player-helper';
import Const from 'constants';
import Action from 'actions';
import {PlayersPoolBuilder} from 'services/players-pool/players-pool-builder';
import {toFloat} from 'helpers/number-helper';
import BasePlayer from 'services/sports/lol/player/player';

export default class Player extends BasePlayer {
    getExpandedEntityParams({incomingValues = {}}) {
        const {modesConfig} = Action.getState();
        const {userPointsType = 'adminPoints'} = incomingValues;
        const expandedPlayer = super.getExpandedEntityParams({incomingValues});
        const userPoints = parseFloat(expandedPlayer[userPointsType]);
        const cptPointCoef = modesConfig.getConfig('CPT').getPointCoefficient();
        const captainUserPoints = userPoints * cptPointCoef;
        const captainAdminPoints = expandedPlayer.adminPoints * cptPointCoef;
        const captainPoints = expandedPlayer.points * cptPointCoef;
        const PlayerService = this;

        return {
            ...expandedPlayer,
            userPoints,
            userScore: calcUserScore(userPoints, expandedPlayer.salary, this.userScoreMultiplier),
            lineupRole: expandedPlayer.positionName,
            captain_exposureMax: expandedPlayer.exposureMax,
            captain_exposureMin: expandedPlayer.exposureMin,
            captain_points: toFloat(captainPoints, 3),
            captain_adminPoints: toFloat(captainAdminPoints, 3),
            captain_userPoints: toFloat(captainUserPoints, 3),
            captain_userScore: calcUserScore(captainUserPoints, expandedPlayer.salary, this.userScoreMultiplier),
            captain_pointsChangedManually: false,
            resetExposure() {
                this.exposureMin = getDefaultExposure('min');
                this.exposureMax = getDefaultExposure('max');
                this.captain_exposureMin = getDefaultExposure('min');
                this.captain_exposureMax = getDefaultExposure('max');
            },
            getPropNameOfMode(prop, incomingMode) {
                let mode = incomingMode || this.mode;

                return PlayerService.exec('definePropNameOfMode', {prop, mode});
            },
            getPropNameOfLineupRole(prop) {
                let propName = prop;

                if (this.getIsCaptain()) {
                    propName = this.getCapPropName(prop);
                }

                return propName;
            },
            getPropValueOfLineupRole(prop) {
                let value,
                    propName = this.getPropNameOfLineupRole(prop);

                if (this.hasOwnProperty(propName)) {
                    value = this[propName];
                } else if (this.hasOwnProperty(prop)) {
                    value = this[prop];
                }

                return value;
            },
            getCapPropName(prop) {
                let mode = this.getIsCaptain() ? Const.CAPTAIN_MODE : Const.DEFAULT_MODE;
                return PlayerService.exec('definePropNameOfMode', {prop, mode});
            },
            getCapPropValue(prop) {
                let value,
                    propName = this.getCapPropName(prop);

                if (this.hasOwnProperty(propName)) {
                    value = this[propName];
                } else if (this.hasOwnProperty(prop)) {
                    value = this[prop];
                }

                return value;
            },
            getIsCaptain() {
                return this.lineupRole === Const.CAPTAIN_MODE;
            },
            setAsCaptain() {
                this.lineupRole = Const.CAPTAIN_MODE;
            },
            getLineupRole() {
                return this.lineupRole;
            },
            isFitForLineup(substitutablePlayer) {
                if (substitutablePlayer.positionInLineup === Const.POSITION_STAR) {
                    return true;
                }
                return substitutablePlayer.positionIdInLineup === this.positionName;
            },
        };
    }

    definePropNameOfMode({incomingValues: {prop, mode}}) {
        let captainPropsList = [
            'exposureMax',
            'exposureMin',
            'points',
            'adminPoints',
            'userPoints',
            'userScore',
            'pointsChangedManually',
        ];
        if (Const.CAPTAIN_MODE === mode && inArray(prop, captainPropsList)) {
            return 'captain_' + prop;
        }
        return prop;
    }

    preparePoolsOfPlayers({incomingValues: {players, filterPlayersBeforePreparingPools}}) {
        if (!filterPlayersBeforePreparingPools) {
            return this.exec('createPoolsOfPlayers', {
                players: Object.values(players),
            });
        }

        const {
            playersData: {filters},
        } = Action.getState();

        const params = {
            team: {
                filterType: Const.FILTER_TYPE_IN_ARRAY,
                filterValue: filters.games,
            },
        };

        players = filterObjectByParams(players, params, true);

        return this.exec('createPoolsOfPlayers', {
            players,
        });
    }

    createPoolsOfPlayers({incomingValues: {players}}) {
        const {playersLock} = Action.getState();

        let builder = new PlayersPoolBuilder(players);

        builder.addTaskOnScenario('position');
        builder.addTaskOnScenario('playerId');
        builder.addTaskOnScenario('teamAndPosition');
        builder.addTaskOnScenario('allowedPositions');
        builder.addTaskOnProperty('team');
        builder.addTaskOnProperty('gameInfo');
        builder.addTaskOnProperty('exposureMin', function (player) {
            return (
                !player.isExcludedByPoints(Const.FLEX_MODE) &&
                !playersLock.isLockedAnywhere(player.getId()) &&
                player.exposureMin > 0
            );
        });
        builder.addTaskOnProperty('exposureMax', function (player) {
            return (
                !player.isExcludedByPoints(Const.FLEX_MODE) &&
                !playersLock.isLockedAnywhere(player.getId()) &&
                player.exposureMax < 100
            );
        });
        builder.addTaskOnProperty('captain_exposureMin', function (player) {
            return (
                !player.isExcludedByPoints(Const.CAPTAIN_MODE) &&
                !playersLock.isLockedAnywhere(player.getId()) &&
                player.captain_exposureMin > 0
            );
        });
        builder.addTaskOnProperty('captain_exposureMax', function (player) {
            return (
                !player.isExcludedByPoints(Const.CAPTAIN_MODE) &&
                !playersLock.isLockedAnywhere(player.getId()) &&
                player.captain_exposureMax < 100
            );
        });
        builder.addTaskOnProperty('overall_exposureMax', function (player) {
            return !playersLock.isLockedAnywhere(player.getId()) && player.overall_exposureMax < 100;
        });

        return builder.execTasks();
    }

    prepareRandomPoints({incomingValues: {}}) {
        return this.exec('prepareRandomPointsByModes', {});
    }

    preparePlayerForLineup({incomingValues = {}}) {
        return this.exec('preparePlayerForLineup_ShowDown', incomingValues);
    }

    resetPoints({incomingValues: {dispatch}}) {
        return this.exec('resetPoints_ShowDown', {dispatch});
    }
}
